import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiChevronDown,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiX,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/header.scss";

function Header() {
  const { cartCount, wishlist } = useContext(CartContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const quietHeader = ["/checkout", "/paysuccess", "/payment"].includes(
    location.pathname
  );

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });

    const titles = {
      "/": `Stationery & journaling | ${siteConfig.brandName}`,
      "/shop": `Shop stationery | ${siteConfig.brandName}`,
      "/about": `About us | ${siteConfig.brandName}`,
      "/contact": `Contact | ${siteConfig.brandName}`,
      "/cartpage": `Shopping bag | ${siteConfig.brandName}`,
      "/checkout": `Checkout | ${siteConfig.brandName}`,
      "/shipping": `Shipping policy | ${siteConfig.brandName}`,
      "/returns": `Returns & refunds | ${siteConfig.brandName}`,
      "/privacy": `Privacy policy | ${siteConfig.brandName}`,
      "/terms": `Terms of service | ${siteConfig.brandName}`,
      "/faqs": `Frequently asked questions | ${siteConfig.brandName}`,
      "/cookies": `Cookie policy | ${siteConfig.brandName}`,
    };
    const titlePath = location.pathname.startsWith("/shopDetails/")
      ? "/shop"
      : location.pathname;
    document.title = titles[titlePath] || siteConfig.brandName;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", siteConfig.description);
  }, [location.pathname, location.search]);

  const handleSearch = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    navigate(query ? `/shop?search=${encodeURIComponent(query)}` : "/shop");
  };

  return (
    <header
      className={`site-header${quietHeader ? " site-header--quiet" : ""}`}
    >
      {!quietHeader && (
        <div className="announcement-bar" aria-label="Store announcements">
          <span>
            Flat-rate shipping from {formatPrice(siteConfig.shipping.flatRate)}
          </span>
          <span>Secure checkout in {siteConfig.currency}</span>
          <span>Original stationery for everyday rituals</span>
        </div>
      )}

      <div className="header-shell">
        <div className="header-utility header-utility--left">
          {!quietHeader && (
            <>
              <span>English</span>
              <span className="header-utility__divider" aria-hidden="true" />
              <span>{siteConfig.currency}</span>
            </>
          )}
        </div>

        <Link
          className="brand-mark"
          to="/"
          aria-label={`${siteConfig.brandName} home`}
        >
          <img src={siteConfig.logo} alt="" width="42" height="42" />
          <span className="visually-hidden">{siteConfig.displayName}</span>
        </Link>

        {!quietHeader && (
          <div className="header-actions">
            <button
              type="button"
              className="icon-button header-search-toggle"
              aria-label="Search"
              aria-expanded={searchOpen}
              onClick={() => setSearchOpen((open) => !open)}
            >
              <FiSearch />
            </button>

            <Link
              className="icon-button count-link desktop-only"
              to="/shop?wishlist=1"
              aria-label={`Wishlist with ${wishlist.length} items`}
            >
              <FiHeart />
              {wishlist.length > 0 && <span>{wishlist.length}</span>}
            </Link>
            <Link
              className="icon-button count-link"
              to="/cartpage"
              aria-label={`Shopping bag with ${cartCount} items`}
            >
              <FiShoppingBag />
              {cartCount > 0 && <span>{cartCount}</span>}
            </Link>
            <button
              type="button"
              className="icon-button mobile-menu-toggle"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <FiX /> : <FiMenu />}
            </button>
          </div>
        )}
      </div>

      {!quietHeader && searchOpen && (
        <form className="header-search" role="search" onSubmit={handleSearch}>
          <label htmlFor="site-search">Search the collection</label>
          <div>
            <input
              id="site-search"
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Try ‘washi tape’ or ‘notebook’"
              autoFocus
            />
            <button type="submit" aria-label="Submit search">
              <FiSearch />
            </button>
          </div>
        </form>
      )}

      {!quietHeader && (
        <nav
          className={`primary-nav${menuOpen ? " primary-nav--open" : ""}`}
          aria-label="Primary navigation"
        >
          <Link to="/">Home</Link>
          <Link to="/shop?collection=new">New arrivals</Link>
          <Link to="/shop?collection=limited">Limited-time picks</Link>
          <Link to="/shop?collection=restock">Restocks</Link>
          <div className="nav-dropdown">
            <Link to="/shop" className="nav-dropdown__trigger">
              All Products <FiChevronDown aria-hidden="true" />
            </Link>
            <div className="nav-dropdown__panel">
              <div>
                <p>Paper & notebooks</p>
                <Link to="/shop?category=Notebooks%20%26%20Journals">
                  Notebooks & journals
                </Link>
                <Link to="/shop?category=Memos%20%26%20Sticky%20Notes">
                  Memos & sticky notes
                </Link>
                <Link to="/shop?category=Paper%20Goods">
                  Letters, cards & paper
                </Link>
              </div>
              <div>
                <p>Create & decorate</p>
                <Link to="/shop?category=Tapes%20%26%20Stickers">
                  Tapes & stickers
                </Link>
                <Link to="/shop?category=Stamps">Stamps</Link>
                <Link to="/shop?category=Writing%20%26%20Drawing">
                  Writing & drawing
                </Link>
              </div>
              <div>
                <p>At your desk</p>
                <Link to="/shop?category=Desk%20Essentials">
                  Desk essentials
                </Link>
                <Link to="/shop?category=Carriers%20%26%20Accessories">
                  Carriers & accessories
                </Link>
                <Link to="/shop">View all products</Link>
              </div>
            </div>
          </div>
          <Link to="/about">About</Link>
          <div className="nav-dropdown nav-dropdown--more">
            <button type="button" className="nav-dropdown__trigger">
              More <FiChevronDown aria-hidden="true" />
            </button>
            <div className="nav-dropdown__panel">
              <div>
                <p>Information</p>
                <Link to="/privacy">Privacy policy</Link>
                <Link to="/cookies">Cookie policy</Link>
                <Link to="/terms">Terms of service</Link>
                <Link to="/faqs">FAQs</Link>
                <Link to="/shipping">Shipping policy</Link>
                <Link to="/returns">Returns & refunds</Link>
              </div>
            </div>
          </div>
          <Link className="mobile-only" to="/shop?wishlist=1">
            Wishlist ({wishlist.length})
          </Link>
          <Link className="mobile-only" to="/contact">
            Customer care
          </Link>
        </nav>
      )}
    </header>
  );
}

export default Header;
